import { RefObject, useEffect } from 'react';
import { _isNil, _notNil } from '@/littledash';

interface UseGroupHeaderScrollParams {
  scrollElementRef: RefObject<HTMLElement>;
  targetElementRef: RefObject<HTMLElement>;
  startOffset: number;
  groupWidth: number;
  pinnedColumnWidth: number;
  actionsWidth: number;
  contentWidth: number;
}

/**
 * Hook to apply scroll effect to group header content based on scroll position.
 * The content stays fixed until it would be hidden by sticky columns, then moves with
 * the scroll until it reaches the column actions on the right.
 * Directly manipulates DOM for better performance.
 */
export function useGroupHeaderScroll({
  scrollElementRef,
  targetElementRef,
  startOffset,
  groupWidth,
  pinnedColumnWidth,
  actionsWidth,
  contentWidth,
}: UseGroupHeaderScrollParams): void {
  useEffect(() => {
    const scrollElement = scrollElementRef.current;
    const targetElement = targetElementRef.current;

    if (_isNil(scrollElement) || _isNil(targetElement)) {
      return;
    }

    const scrollThreshold = startOffset - pinnedColumnWidth;
    const availableSpace = groupWidth - actionsWidth - contentWidth;
    const maxTranslation = Math.max(0, availableSpace);

    let rafId: number | null = null;
    const abortController = new AbortController();

    const updateTransform = () => {
      const scrollPosition = scrollElement.scrollLeft;
      const scrollBeyondThreshold = Math.max(0, scrollPosition - scrollThreshold);
      const translation = Math.min(scrollBeyondThreshold, maxTranslation);

      targetElement.style.transform = `translateX(${translation}px)`;
      rafId = null;
    };

    const handleScroll = () => {
      if (_isNil(rafId)) {
        rafId = requestAnimationFrame(updateTransform);
      }
    };

    // Set initial transform
    updateTransform();

    // Add scroll listener
    scrollElement.addEventListener('scroll', handleScroll, {
      passive: true,
      signal: abortController.signal,
    });

    return () => {
      abortController.abort();
      if (_notNil(rafId)) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [scrollElementRef, targetElementRef, startOffset, groupWidth, pinnedColumnWidth, actionsWidth, contentWidth]);
}
